import { useEffect, useRef, useState } from "react";
import { Plus } from "lucide-react";
import toast from "react-hot-toast";
import { DsButton, DsIcon } from "../../../../design-system";
import SalaryComponentFormModal from "./SalaryComponentFormModal";

const CATEGORY_STYLES = {
  EARNING: "bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]",
  DEDUCTION: "bg-[hsl(var(--ds-danger)/0.12)] text-[hsl(var(--ds-danger))]",
  BENEFIT: "bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))]",
  EMPLOYER_BENEFIT: "bg-[hsl(var(--ds-accent))] text-[hsl(var(--ds-secondary))]",
  REIMBURSEMENT: "bg-[hsl(var(--ds-info)/0.12)] text-[hsl(var(--ds-info))]",
};

function categoryClass(category, darkMode) {
  if (darkMode) {
    if (category === "EARNING") return "bg-[hsl(var(--ds-success)/0.2)] text-[hsl(var(--ds-success))]";
    if (category === "DEDUCTION") return "bg-[hsl(var(--ds-danger)/0.2)] text-[hsl(var(--ds-danger))]";
    if (category === "REIMBURSEMENT") return "bg-[hsl(var(--ds-info)/0.2)] text-[hsl(var(--ds-info))]";
    return "bg-[hsl(var(--ds-secondary))]/60 text-[hsl(var(--ds-accent-fg))]";
  }
  return CATEGORY_STYLES[category] || "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-fg))]";
}

/** Persist only fields the create/edit form supports (no invented calc type / tax flags). */
function toLibraryComponent(payload, existing = null) {
  const name = payload.name || "";
  const isBasic =
    /^basic(\s+salary|\s+pay)?$/i.test(name.trim()) ||
    String(payload.code || "").toUpperCase() === "BASIC";
  const sortOrder = isBasic
    ? 1
    : Number.isFinite(Number(payload.sortOrder))
      ? Number(payload.sortOrder)
      : Number.isFinite(Number(existing?.sortOrder))
        ? Number(existing.sortOrder)
        : 0;
  return {
    id: existing?.id ?? Date.now(),
    name,
    code: (name || "").toUpperCase().replace(/\s+/g, "_"),
    category: payload.category,
    notes: payload.notes || "",
    sortOrder,
    isActive: existing?.isActive !== false,
    tdsReferenceHeadId:
      payload.tdsReferenceHeadId ?? existing?.tdsReferenceHeadId ?? null,
    tdsReferenceHead: payload.tdsReferenceHead ?? existing?.tdsReferenceHead ?? null,
    flexiEnabled: payload.flexiEnabled ?? existing?.flexiEnabled ?? false,
    flexiConfigJson:
      payload.flexiConfigJson ?? existing?.flexiConfigJson ?? existing?.flexiConfig ?? null,
  };
}

/** Strip legacy fields (nature, calculationType, taxable flags) that were never on the create form. */
function sanitizeLibraryComponent(comp) {
  return toLibraryComponent(
    {
      name: comp.name || "",
      category: comp.category || "EARNING",
      notes: comp.notes || "",
      sortOrder: comp.sortOrder,
      code: comp.code,
      tdsReferenceHeadId: comp.tdsReferenceHeadId,
      flexiEnabled: comp.flexiEnabled,
      flexiConfigJson: comp.flexiConfigJson,
    },
    { id: comp.id, isActive: comp.isActive, sortOrder: comp.sortOrder }
  );
}

export default function SalaryComponentsPanel({
  components = [],
  onSaveComponents,
  onCreateComponent,
  onUpdateComponent,
  onDeleteComponent,
  darkMode = false,
}) {
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingComponent, setEditingComponent] = useState(null);
  const [openMenuId, setOpenMenuId] = useState(null);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!openMenuId) return undefined;
    const onDocClick = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setOpenMenuId(null);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [openMenuId]);

  const persistCleanList = (nextList) => {
    if (typeof onSaveComponents === "function") {
      onSaveComponents(nextList.map(sanitizeLibraryComponent));
    }
  };

  const handleCreate = async (payload) => {
    const draft = toLibraryComponent(payload);
    try {
      if (typeof onCreateComponent === "function") {
        await onCreateComponent(draft);
      } else {
        persistCleanList([...components, draft]);
      }
      setShowCreateModal(false);
      toast.success("Component created successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to create component");
    }
  };

  const handleUpdate = async (payload) => {
    if (!editingComponent) return;
    const updated = toLibraryComponent(payload, editingComponent);
    try {
      if (typeof onUpdateComponent === "function") {
        await onUpdateComponent(updated.id, updated);
      } else {
        persistCleanList(components.map((c) => (c.id === updated.id ? updated : c)));
      }
      setEditingComponent(null);
      setOpenMenuId(null);
      toast.success("Component updated successfully!");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to update component");
    }
  };

  const toggleActive = async (component) => {
    const nextActive = !(component.isActive !== false);
    try {
      if (typeof onUpdateComponent === "function") {
        await onUpdateComponent(component.id, {
          ...sanitizeLibraryComponent(component),
          isActive: nextActive,
        });
      } else {
        persistCleanList(
          components.map((c) =>
            c.id === component.id
              ? { ...sanitizeLibraryComponent(c), isActive: nextActive }
              : c
          )
        );
      }
      setOpenMenuId(null);
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to update component");
    }
  };

  const handleDelete = async (component) => {
    if (!window.confirm(`Delete "${component.name}"? This cannot be undone.`)) return;
    try {
      if (typeof onDeleteComponent === "function") {
        await onDeleteComponent(component.id);
      }
      setOpenMenuId(null);
      toast.success("Component deleted.");
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Failed to delete component");
    }
  };

  const openEdit = (component) => {
    setOpenMenuId(null);
    setEditingComponent(component);
  };

  return (
    <div
      className={`flex h-full flex-col rounded-xl border shadow-sm ${
        darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
      }`}
    >
      <div
        className={`flex items-center justify-between border-b px-4 py-3 ${
          darkMode ? "border-[hsl(var(--ds-border))]" : "border-[hsl(var(--ds-border))]"
        }`}
      >
        <div>
          <h3 className={`text-sm font-semibold ${darkMode ? "text-white" : "text-[hsl(var(--ds-fg))]"}`}>
            Salary Components
          </h3>
          <p className={`text-[11px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
            {components.length} component{components.length !== 1 ? "s" : ""}
          </p>
        </div>
        <DsButton
          type="button"
          size="sm"
          variant="primary"
          onClick={() => setShowCreateModal(true)}
          title="Add Component"
        >
          <DsIcon icon={Plus} size="sm" />
          Add
        </DsButton>
      </div>

      <div className="min-h-0 flex-1 overflow-auto p-3">
        {components.length === 0 ? (
          <div
            className={`flex flex-col items-center justify-center rounded-xl border border-dashed py-10 text-center ${
              darkMode ? "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]" : "border-[hsl(var(--ds-border))] text-[hsl(var(--ds-muted-fg))]"
            }`}
          >
            <p className="text-sm font-medium">No components yet</p>
            <p className="mt-1 text-xs">Click Add to create your first salary component.</p>
          </div>
        ) : (
          <div className="space-y-2">
            <div
              className={`hidden gap-2 rounded-lg px-3 py-2 text-[10px] font-semibold uppercase tracking-wide lg:grid lg:grid-cols-[minmax(0,1.4fr)_minmax(0,0.6fr)_minmax(0,1fr)_auto] ${
                darkMode ? "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]" : "bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
              }`}
            >
              <span>Name / Code</span>
              <span>Order</span>
              <span>Category</span>
              <span className="text-right">Actions</span>
            </div>

            {components.map((component) => (
              <div
                key={component.id}
                className={`rounded-xl border px-3 py-2.5 transition-shadow hover:shadow-md ${
                  component.isActive !== false
                    ? darkMode
                      ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))]"
                      : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                    : darkMode
                      ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] opacity-70"
                      : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] opacity-75"
                }`}
              >
                <div className="grid grid-cols-1 items-center gap-2 lg:grid-cols-[minmax(0,1.4fr)_minmax(0,0.6fr)_minmax(0,1fr)_auto]">
                  <div className="min-w-0">
                    <p className={`truncate text-sm font-medium ${darkMode ? "text-white" : "text-[hsl(var(--ds-fg))]"}`}>
                      {component.name || "Unnamed"}
                    </p>
                    <p className={`truncate text-[11px] ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-muted-fg))]"}`}>
                      {component.code || "—"}
                    </p>
                    {component.isActive === false && (
                      <span className="mt-0.5 inline-block rounded-full bg-[hsl(var(--ds-muted))] px-2 py-0.5 text-[10px] font-medium text-[hsl(var(--ds-muted-fg))]">
                        Inactive
                      </span>
                    )}
                  </div>

                  <div className={`text-sm tabular-nums ${darkMode ? "text-[hsl(var(--ds-muted-fg))]" : "text-[hsl(var(--ds-fg))]"}`}>
                    {component.sortOrder != null && Number(component.sortOrder) > 0
                      ? component.sortOrder
                      : "—"}
                  </div>

                  <div>
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-[10px] font-medium ${categoryClass(
                        component.category,
                        darkMode
                      )}`}
                    >
                      {component.category || "—"}
                    </span>
                  </div>

                  <div className="relative flex items-center justify-end" ref={openMenuId === component.id ? menuRef : null}>
                    <button
                      type="button"
                      onClick={() => setOpenMenuId(openMenuId === component.id ? null : component.id)}
                      className={`rounded-lg p-1.5 transition-colors ${
                        darkMode ? "hover:bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-muted-fg))]" : "hover:bg-[hsl(var(--ds-muted))] text-[hsl(var(--ds-muted-fg))]"
                      }`}
                      aria-label="More actions"
                      aria-expanded={openMenuId === component.id}
                    >
                      <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
                      </svg>
                    </button>

                    {openMenuId === component.id && (
                      <div
                        className={`absolute right-0 top-8 z-20 min-w-[9rem] overflow-hidden rounded-xl border shadow-lg ${
                          darkMode ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]" : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))]"
                        }`}
                      >
                        <button
                          type="button"
                          onClick={() => openEdit(component)}
                          className={`block w-full px-3 py-2 text-left text-xs font-medium ${
                            darkMode ? "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]" : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                          }`}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => toggleActive(component)}
                          className={`block w-full px-3 py-2 text-left text-xs ${
                            darkMode ? "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]" : "text-[hsl(var(--ds-fg))] hover:bg-[hsl(var(--ds-muted))]"
                          }`}
                        >
                          {component.isActive !== false ? "Deactivate" : "Activate"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(component)}
                          className={`block w-full px-3 py-2 text-left text-xs ${
                            darkMode ? "text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-muted))]" : "text-[hsl(var(--ds-danger))] hover:bg-[hsl(var(--ds-danger)/0.08)]"
                          }`}
                        >
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <SalaryComponentFormModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreate}
        darkMode={darkMode}
        title="Create New Component"
        submitLabel="Create Component"
      />

      <SalaryComponentFormModal
        isOpen={!!editingComponent}
        onClose={() => setEditingComponent(null)}
        onSubmit={handleUpdate}
        initialData={editingComponent}
        darkMode={darkMode}
        title="Edit Component"
        submitLabel="Update Component"
      />
    </div>
  );
}
